import {
  ANALYTICS_ALL_EVENTS,
  ANALYTICS_SERIES_MAX_EVENTS,
  type AnalyticsDimension,
  type AnalyticsGranularity,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@loot-os/ui/components/chart";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@loot-os/ui/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Link2, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import { ErrorAlert } from "../components/ErrorAlert";
import { TableMessageRow } from "../components/TableMessageRow";
import {
  BREAKDOWN_NONE,
  BREAKDOWN_OPTIONS,
  type BreakdownRow,
  breakdownHint,
  breakdownLabel,
  breakdownSeries,
  breakdownTableRows,
  pivotBreakdown,
} from "../lib/analyticsBreakdown";
import {
  browserEventsHint,
  CHART_REFUSAL_MESSAGES,
  chartRefusal,
  INSIGHT_CHART_LABELS,
  INSIGHT_CHARTS,
  INSIGHT_EVENT_GROUPS,
  INSIGHT_MEASURE_LABELS,
  INSIGHT_MEASURES,
  INSIGHT_PRESETS,
  type InsightChart,
  type InsightChartRow,
  type InsightMeasure,
  type InsightsSearch,
  insightEventLabel,
  insightSearch,
  insightSeries,
  insightTableRows,
  joinEventNames,
  parseInsightsSearch,
  pivotSeries,
  resolveInsight,
  type SeriesByEventRow,
  toggleEventName,
} from "../lib/analyticsInsights";
import { useActiveEnvironmentId } from "../lib/environment";
import { formatNumber, formatRelativeTime } from "../lib/format";
import {
  ANALYTICS_WINDOW_LABELS,
  ANALYTICS_WINDOWS,
  type AnalyticsWindow,
  axisTicks,
  bucketLabel,
  enumerateBuckets,
  granularityOf,
  tickLabel,
  windowRange,
} from "../lib/timeBuckets";
import { useTRPC } from "../lib/trpc";

const NO_ROWS: readonly SeriesByEventRow[] = [];
const NO_BREAKDOWN_ROWS: readonly BreakdownRow[] = [];

/** What the plot needs of a series, whether it is an event or a breakdown's value. */
interface PlotSeries {
  key: string;
  label: string;
  color: string;
}

/** Axis ticks only: "1.2K". Every other number on the page is exact. */
const axisNumber = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** One labelled control of the builder. The label names a group of tabs, so it is not a `<label>`. */
function BuilderField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      <p className="text-sm font-medium">{label}</p>
      {children}
    </div>
  );
}

/**
 * The searchable event list. It stays open across picks, since an insight usually takes several,
 * and at the cap the events not yet picked are disabled rather than hidden.
 */
function EventPicker({
  eventNames,
  single,
  onToggle,
}: {
  eventNames: readonly string[];
  /** A breakdown charts one event: a pick replaces it and closes the list. */
  single: boolean;
  onToggle: (eventName: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const atCap = !single && eventNames.length >= ANALYTICS_SERIES_MAX_EVENTS;
  const item = (eventName: string, keywords: string[]) => {
    const picked = eventNames.includes(eventName);
    return (
      <CommandItem
        key={eventName}
        value={insightEventLabel(eventName)}
        keywords={keywords}
        disabled={atCap && !picked}
        onSelect={() => {
          onToggle(eventName);
          if (single) setOpen(false);
        }}
      >
        <Check className={picked ? "opacity-100" : "opacity-0"} />
        <span className="shrink-0 whitespace-nowrap">{insightEventLabel(eventName)}</span>
        {eventName !== ANALYTICS_ALL_EVENTS && (
          <span className="ml-auto min-w-0 truncate pl-3 font-mono text-xs text-muted-foreground">
            {eventName}
          </span>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            className="justify-between font-normal"
          />
        }
      >
        {single
          ? "Change the event"
          : atCap
            ? `${ANALYTICS_SERIES_MAX_EVENTS} of ${ANALYTICS_SERIES_MAX_EVENTS} events`
            : "Add an event"}
        <ChevronsUpDown className="opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-96 max-w-[calc(100vw-2rem)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search events…" />
          <CommandList>
            <CommandEmpty>No event found.</CommandEmpty>
            <CommandGroup>{item(ANALYTICS_ALL_EVENTS, ["total", "everything", "*"])}</CommandGroup>
            {INSIGHT_EVENT_GROUPS.map((group) => (
              <div key={group.source}>
                <CommandSeparator />
                <CommandGroup heading={group.heading}>
                  {group.eventNames.map((eventName) => item(eventName, [eventName]))}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The plot: lines, or bars side by side or stacked, on one y-axis (an insight has one measure).
 * `chart` arrives as one the selection may be drawn as (`resolveInsight`), so stacked here is
 * always events that partition (several events, or one event's values under a breakdown), never
 * shoppers and never All events on top of its own parts.
 */
function InsightPlot({
  chart,
  series,
  data,
  granularity,
}: {
  chart: InsightChart;
  series: readonly PlotSeries[];
  data: InsightChartRow[];
  granularity: AnalyticsGranularity;
}) {
  const config = useMemo<ChartConfig>(
    () => Object.fromEntries(series.map((s) => [s.key, { label: s.label, color: s.color }])),
    [series],
  );
  const ticks = axisTicks(data.map((row) => String(row.bucket)));

  const xAxis = (
    <XAxis
      dataKey="bucket"
      tickLine={false}
      axisLine={false}
      tickMargin={8}
      ticks={ticks}
      // Both edge labels stay: the default drops a first tick that overhangs the plot.
      interval="preserveStartEnd"
      tickFormatter={(value: string) => tickLabel(granularity, new Date(value))}
      className="text-[10px]"
    />
  );
  const yAxis = (
    <YAxis
      allowDecimals={false}
      tickLine={false}
      axisLine={false}
      width={44}
      tickFormatter={(value: number) => axisNumber.format(value)}
      className="text-[10px]"
    />
  );
  const tooltip = (
    <ChartTooltip
      content={
        <ChartTooltipContent
          labelFormatter={(label) =>
            typeof label === "string" ? bucketLabel(granularity, new Date(label)) : label
          }
          formatter={(value, name) => (
            <>
              <div
                className="h-2 w-2 shrink-0 rounded-[2px]"
                style={{ backgroundColor: `var(--color-${name})` }}
              />
              {config[String(name)]?.label ?? name}
              <div className="ml-auto pl-4 font-mono font-medium tabular-nums text-foreground">
                {formatNumber(Number(value))}
              </div>
            </>
          )}
        />
      }
    />
  );
  // A series carries no `name`: the tooltip looks its label up by data key. Animation is off
  // because the page polls while a rollup runs, and would replay it each time.
  return (
    <>
      <ChartContainer config={config} className="h-72 w-full">
        {chart === "line" ? (
          <LineChart data={data} margin={{ left: 0, right: 8, top: 8 }}>
            <CartesianGrid vertical={false} />
            {xAxis}
            {yAxis}
            {tooltip}
            {series.map((s) => (
              <Line
                key={s.key}
                dataKey={s.key}
                // Straight segments: a smoothed curve from an empty bucket to the next one draws a
                // ramp of counts between them that nothing recorded.
                type="linear"
                strokeLinejoin="round"
                strokeLinecap="round"
                stroke={`var(--color-${s.key})`}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        ) : (
          <BarChart data={data} barGap={2} margin={{ left: 0, right: 8, top: 8 }}>
            <CartesianGrid vertical={false} />
            {xAxis}
            {yAxis}
            {tooltip}
            {series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                fill={`var(--color-${s.key})`}
                maxBarSize={16}
                isAnimationActive={false}
                {...(chart === "stacked"
                  ? // A hairline of the card's color separates the segments of one bar.
                    { stackId: "events", stroke: "var(--card)", strokeWidth: 1 }
                  : { radius: [4, 4, 0, 0] as [number, number, number, number] })}
              />
            ))}
          </BarChart>
        )}
      </ChartContainer>
      {/* The legend is HTML under the plot rather than the chart's own: that one reserves a single
          row, so a wrapped second row would sit on the axis labels. One series is named by the
          card's title and gets none. */}
      {series.length > 1 && (
        <ul aria-label="Series" className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-3">
          {series.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5 text-xs">
              <span
                className="size-2 shrink-0 rounded-[2px]"
                style={{ backgroundColor: s.color }}
              />
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function AnalyticsInsightsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const navigate = useNavigate({ from: "/tenants/$tenantId/analytics/insights" });
  // The URL is the whole insight. The environment rides the x-loot-environment header.
  const { w, m, c, e, b } = useSearch({ strict: false }) as InsightsSearch;
  const insight = useMemo(() => resolveInsight({ w, m, c, e, b }), [w, m, c, e, b]);
  const breakdown = insight.breakdown;
  const brokenDownEvent = insight.eventNames[0] ?? ANALYTICS_ALL_EVENTS;
  const granularity = granularityOf(insight.window);
  const perBucket = granularity === "hour" ? "hour" : "day";

  // Every change goes back through the parser, so picking Shoppers while stacked, or adding
  // All events beside others, lands on a legal chart rather than an illegal URL.
  const update = (patch: InsightsSearch) => {
    const next = resolveInsight(parseInsightsSearch({ ...insightSearch(insight), ...patch }));
    void navigate({ to: ".", search: insightSearch(next), replace: true });
  };
  // One pick in the event list: under a breakdown it replaces the one event, else it toggles.
  const pickEvent = (eventName: string) =>
    update({
      e: breakdown ? eventName : joinEventNames(toggleEventName(insight.eventNames, eventName)),
    });

  // One "now" per window choice, so both query inputs agree and do not churn per render.
  const { now, from, to } = useMemo(() => {
    const at = new Date();
    return { now: at, ...windowRange(insight.window, at) };
  }, [insight.window]);
  const range = { granularity, from, to };

  // The summary is read for its freshness alone: when the rollups last landed, and whether a
  // run is in progress, during which both queries poll so the numbers land on their own.
  const summary = useQuery(
    trpc.analytics.summary.queryOptions(range, {
      placeholderData: (previous) => previous,
      refetchInterval: (query) => (query.state.data?.lastRunStatus === "running" ? 4000 : false),
    }),
  );
  const running = summary.data?.lastRunStatus === "running";
  // One of the two reads runs: the events' own rollups, or the one event's breakdown rollups.
  const seriesByEvent = useQuery(
    trpc.analytics.seriesByEvent.queryOptions(
      { ...range, eventNames: insight.eventNames },
      {
        enabled: !breakdown,
        placeholderData: (previous) => previous,
        refetchInterval: running ? 4000 : false,
      },
    ),
  );
  const byValue = useQuery(
    trpc.analytics.breakdown.queryOptions(
      { ...range, eventName: brokenDownEvent, dimension: breakdown ?? "os" },
      {
        enabled: !!breakdown,
        placeholderData: (previous) => previous,
        refetchInterval: running ? 4000 : false,
      },
    ),
  );
  const read = breakdown ? byValue : seriesByEvent;

  // A run that lands changes "updated at"; the series read rollups the same run wrote (a fast
  // local run can finish before the page ever sees `running`, so polling alone would miss it).
  const updatedAt = summary.data?.updatedAt ?? null;
  useEffect(() => {
    if (!updatedAt) return;
    void queryClient.invalidateQueries(trpc.analytics.seriesByEvent.pathFilter());
    void queryClient.invalidateQueries(trpc.analytics.breakdown.pathFilter());
  }, [updatedAt, queryClient, trpc]);

  // The active environment's analytics mode, from the list the environment switcher already
  // keeps cached, resolved as the switcher resolves it.
  const storedEnvironmentId = useActiveEnvironmentId();
  const environments = useQuery(trpc.environments.list.queryOptions());
  const environment =
    environments.data?.find((env) => env.id === storedEnvironmentId) ??
    environments.data?.find((env) => env.type === "live");
  const hint = browserEventsHint(environment?.analyticsMode, insight.eventNames);

  const rows = seriesByEvent.data ?? NO_ROWS;
  const series = useMemo(() => insightSeries(insight.eventNames), [insight.eventNames]);
  const valueRows = byValue.data ?? NO_BREAKDOWN_ROWS;
  const values = useMemo(
    () =>
      breakdown
        ? breakdownSeries({ rows: valueRows, dimension: breakdown, measure: insight.measure })
        : null,
    [breakdown, valueRows, insight.measure],
  );
  const plotSeries: readonly PlotSeries[] = values ? values.series : series;
  const chartData = useMemo(() => {
    const buckets = enumerateBuckets(granularity, from, to);
    return values
      ? pivotBreakdown({
          rows: valueRows,
          series: values.series,
          measure: insight.measure,
          buckets,
        })
      : pivotSeries({ rows, series, measure: insight.measure, buckets });
  }, [values, valueRows, rows, series, insight.measure, granularity, from, to]);
  const tableRows = useMemo(
    () => insightTableRows({ rows, series, measure: insight.measure }),
    [rows, series, insight.measure],
  );
  const valueTableRows = useMemo(
    () =>
      breakdown && values
        ? breakdownTableRows({
            rows: valueRows,
            series: values.series,
            dimension: breakdown,
            measure: insight.measure,
          })
        : [],
    [breakdown, values, valueRows, insight.measure],
  );

  // Why each chart this selection cannot be drawn as is off, in tab order.
  const refusals = INSIGHT_CHARTS.map((chart) => chartRefusal(chart, insight));
  const onlyAllEvents =
    insight.eventNames.length === 1 && insight.eventNames[0] === ANALYTICS_ALL_EVENTS;
  // Dimmed while the previous selection's rows stand in for the next one's.
  const isStale = read.isLoading || read.isPlaceholderData;
  const isEmpty =
    read.isSuccess && !read.isPlaceholderData && (breakdown ? valueRows : rows).length === 0;
  const error = read.error ?? summary.error;
  const measureLabel = INSIGHT_MEASURE_LABELS[insight.measure];
  const columns = insight.measure === "events" ? 5 : 4;

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(
      () => toast.success("Link copied. It reopens this insight."),
      () => toast.error("Could not copy the link; copy the address bar instead."),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Insights</h1>
        <p className="text-sm text-muted-foreground">
          Chart up to {ANALYTICS_SERIES_MAX_EVENTS} events against each other over time, or break
          one event down by operating system, device, country or customer segment, from this
          environment's analytics rollups. The address bar holds the whole insight, so a link
          reopens it.
        </p>
      </div>

      {error && <ErrorAlert message={error.message} />}

      <Card>
        <CardHeader>
          <CardTitle>Build an insight</CardTitle>
          <CardDescription className="flex flex-wrap items-center gap-x-1 gap-y-1">
            Start from
            {INSIGHT_PRESETS.map((preset) => (
              <Link
                key={preset.label}
                to="/tenants/$tenantId/analytics/insights"
                params={{ tenantId }}
                search={preset.search}
                replace
                className={buttonVariants({
                  variant: "link",
                  size: "sm",
                  className: "h-auto px-1",
                })}
              >
                {preset.label}
              </Link>
            ))}
          </CardDescription>
          <CardAction>
            <Button type="button" variant="outline" size="sm" onClick={copyLink}>
              <Link2 />
              Copy link
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="grid gap-5">
          <BuilderField label={breakdown ? "Event" : "Events"}>
            <div className="flex flex-wrap items-center gap-2">
              {series.map((s) => (
                <Badge key={s.key} variant="outline" className="h-8 gap-1.5 pr-1 text-sm">
                  {/* Under a breakdown the colors belong to the values, not to the event. */}
                  {!breakdown && (
                    <span
                      className="size-2 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: s.color }}
                    />
                  )}
                  {s.label}
                  {!onlyAllEvents && !breakdown && (
                    <button
                      type="button"
                      aria-label={`Remove ${s.label}`}
                      className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        update({
                          e: joinEventNames(toggleEventName(insight.eventNames, s.eventName)),
                        })
                      }
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </Badge>
              ))}
              <EventPicker
                eventNames={insight.eventNames}
                single={breakdown !== null}
                onToggle={pickEvent}
              />
            </div>
          </BuilderField>

          <BuilderField label="Break down by">
            <Tabs
              value={breakdown ?? BREAKDOWN_NONE}
              onValueChange={(v) =>
                // `b: undefined` clears the key; a dimension cuts the events to the first.
                update({ b: v === BREAKDOWN_NONE ? undefined : (v as AnalyticsDimension) })
              }
            >
              <TabsList aria-label="Break down by">
                {BREAKDOWN_OPTIONS.map((option) => (
                  <TabsTrigger key={option.value} value={option.value}>
                    {option.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {breakdown && (
              <p className="text-xs text-muted-foreground">
                A breakdown charts one event, one series per value, from rollups computed with the
                rest. Two dimensions at once are not rolled up.
              </p>
            )}
          </BuilderField>

          <div className="flex flex-wrap gap-x-8 gap-y-5">
            <BuilderField label="Measure">
              <Tabs
                value={insight.measure}
                onValueChange={(v) => update({ m: v as InsightMeasure })}
              >
                <TabsList aria-label="Measure">
                  {INSIGHT_MEASURES.map((measure) => (
                    <TabsTrigger key={measure} value={measure}>
                      {INSIGHT_MEASURE_LABELS[measure]}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </BuilderField>
            <BuilderField label="Chart">
              <Tabs value={insight.chart} onValueChange={(v) => update({ c: v as InsightChart })}>
                <TabsList aria-label="Chart">
                  {INSIGHT_CHARTS.map((chart, i) => (
                    <TabsTrigger key={chart} value={chart} disabled={refusals[i] != null}>
                      {INSIGHT_CHART_LABELS[chart]}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </BuilderField>
            <BuilderField label="Window">
              <Tabs
                value={insight.window}
                onValueChange={(v) => update({ w: v as AnalyticsWindow })}
              >
                <TabsList aria-label="Window">
                  {ANALYTICS_WINDOWS.map((window) => (
                    <TabsTrigger key={window} value={window}>
                      {ANALYTICS_WINDOW_LABELS[window]}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </BuilderField>
          </div>

          {/* A disabled tab takes no hover, so its reason is said here rather than in a tooltip. */}
          {refusals.map(
            (refusal) =>
              refusal && (
                <p key={refusal} className="text-xs text-muted-foreground">
                  {CHART_REFUSAL_MESSAGES[refusal]}
                </p>
              ),
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {breakdown
              ? `${series[0]?.label} by ${breakdownLabel(breakdown).toLowerCase()}: ${measureLabel.toLowerCase()}`
              : series.length === 1
                ? `${series[0]?.label}: ${measureLabel.toLowerCase()}`
                : measureLabel}{" "}
            per {perBucket}
          </CardTitle>
          <CardDescription>
            Trailing {ANALYTICS_WINDOW_LABELS[insight.window]}, UTC; the last {perBucket} is still
            in progress.{" "}
            {insight.measure === "users" &&
              `Shoppers are distinct per ${perBucket} and are never added across ${perBucket}s, events or values. `}
            Hourly analytics,{" "}
            {running
              ? "computing now"
              : updatedAt
                ? `updated ${formatRelativeTime(updatedAt, now)}`
                : "not yet computed"}
            .
          </CardDescription>
        </CardHeader>
        <CardContent
          aria-busy={isStale}
          className={isStale ? "opacity-60 transition-opacity" : "transition-opacity"}
        >
          <InsightPlot
            chart={insight.chart}
            series={plotSeries}
            data={chartData}
            granularity={granularity}
          />
          {values && values.uncharted > 0 && (
            <p className="pt-4 text-xs text-muted-foreground">
              {values.uncharted} more {values.uncharted === 1 ? "value is" : "values are"} in the
              table and not on the chart: shoppers under different values cannot be added into an
              "Other".
            </p>
          )}
          {breakdown && (
            <p className="pt-4 text-xs text-muted-foreground">{breakdownHint(breakdown)}</p>
          )}
          {hint && <p className="pt-4 text-xs text-muted-foreground">{hint}</p>}
        </CardContent>
      </Card>

      {breakdown ? (
        <Card>
          <CardHeader>
            <CardTitle>By {breakdownLabel(breakdown).toLowerCase()}</CardTitle>
            <CardDescription>
              Every value seen over the window, charted or not. The average and the peak are per{" "}
              {perBucket}, over the {perBucket}s with data.
              {insight.measure === "users"
                ? ` Shoppers have no total, across ${perBucket}s or across values: one shopper can be under two values.`
                : " The totals add up to the event's own count."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table aria-busy={isStale} className={isStale ? "opacity-60 transition-opacity" : ""}>
              <TableHeader>
                <TableRow>
                  <TableHead>{breakdownLabel(breakdown)}</TableHead>
                  <TableHead>Value</TableHead>
                  {insight.measure === "events" && (
                    <TableHead className="text-right">Total</TableHead>
                  )}
                  <TableHead className="text-right">Avg per {perBucket}</TableHead>
                  <TableHead className="text-right">Peak {perBucket}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {read.isLoading && <TableMessageRow colSpan={columns}>Loading…</TableMessageRow>}
                {isEmpty && (
                  <TableMessageRow colSpan={columns}>
                    Nothing recorded for this event in this window yet.
                  </TableMessageRow>
                )}
                {!read.isLoading &&
                  !isEmpty &&
                  valueTableRows.map((row) => (
                    <TableRow key={row.value}>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          <span
                            className="size-2 shrink-0 rounded-[2px]"
                            style={{ backgroundColor: row.color ?? "transparent" }}
                          />
                          {row.label}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {row.value}
                      </TableCell>
                      {row.measure === "events" && (
                        <TableCell className="text-right font-mono text-xs">
                          {formatNumber(row.total)}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-mono text-xs">
                        {formatNumber(row.avgPerBucket)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {formatNumber(row.peakPerBucket)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>By event</CardTitle>
            <CardDescription>
              Each event over the window. The average and the peak are per {perBucket}, over the{" "}
              {perBucket}s with data, as the Analytics page's cards read them.
              {insight.measure === "users" &&
                ` Shoppers have no total: the same shopper on two ${perBucket}s is one shopper, which per-${perBucket} counts cannot say.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table aria-busy={isStale} className={isStale ? "opacity-60 transition-opacity" : ""}>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Name</TableHead>
                  {insight.measure === "events" && (
                    <TableHead className="text-right">Total</TableHead>
                  )}
                  <TableHead className="text-right">Avg per {perBucket}</TableHead>
                  <TableHead className="text-right">Peak {perBucket}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {seriesByEvent.isLoading && (
                  <TableMessageRow colSpan={columns}>Loading…</TableMessageRow>
                )}
                {isEmpty && (
                  <TableMessageRow colSpan={columns}>
                    Nothing recorded for these events in this window yet.
                  </TableMessageRow>
                )}
                {!seriesByEvent.isLoading &&
                  !isEmpty &&
                  tableRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          <span
                            className="size-2 shrink-0 rounded-[2px]"
                            style={{ backgroundColor: row.color }}
                          />
                          {row.label}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {row.eventName}
                      </TableCell>
                      {row.measure === "events" && (
                        <TableCell className="text-right font-mono text-xs">
                          {formatNumber(row.total)}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-mono text-xs">
                        {formatNumber(row.avgPerBucket)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {formatNumber(row.peakPerBucket)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
